import CircleIcon from '@mui/icons-material/Circle';
import { Timeline, TimelineItem } from '@mui/lab';
import { Box, Grid, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ActivityLogDTO,
  ComposableDashboardFiltersEnum,
  getJobById,
  getOverlapTeams,
  getRecruiterActivitLogs,
  getUserByEmail,
  IComposableAnalyticsGlobalFilters,
} from 'wisestep-api';
import {
  formatDateInDayMonthYearTime,
  ScrollableButtonTabs,
  WsColors,
  WsTimeLine,
  WsTitleBanner,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import { WsActivityProps } from 'wisestep-base/dist/components/WsTimeLine/WsTimeLine';

import { AccessControl, PinDashboardCollectionButton } from '../../../components';
import { NotesParent } from '../../../components/communicationBar/Notes';
import { WsBreadCrumbs } from '../../../components/WsBreadCrumbs';
import { WsTabs } from '../../../components/WsTabs';
import ComposeNote from '../../../features/clientDetails/components/notes/ComposeNote';
import { filteredTabNames } from '../../../features/recruiterProfileDetails/components/userProfile/helper/profileHelper';
import UserProfileOverview from '../../../features/recruiterProfileDetails/components/userProfile/UserProfileOverview';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { getDashBoardBreadCrumbList, IDashBoardBreadCrumbTypes } from '../helper/defaultValues';

const AccountManagerDetailsPage = () => {
  const [params] = useSearchParams();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const isOverLap = String(params.get('isOverLap'));
  const accountManagerId = String(params.get('id'));
  const name = String(params.get('name'));
  const jobId = String(params.get('jobId'));
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [activityLogData, setActivityLogData] = useState<ActivityLogDTO[]>([]);
  const [activityLists, setActivityLists] = useState<WsActivityProps[]>([]);
  const [isOverLAP, setIsOverLAP] = useState<string>(isOverLap);
  const [accountManagerName, setAccountManagerName] = useState<string>(name);
  const getFilters = useMemo(() => {
    const filters = {} as Record<ComposableDashboardFiltersEnum, IComposableAnalyticsGlobalFilters>;
    filters[ComposableDashboardFiltersEnum.ACCOUNT_MANAGER] = {
      accountManagers: [accountManagerId],
    };
    return filters;
  }, [accountManagerId]);

  useEffect(() => {
    const fetchData = async () => {
      const currentUserTeams = await getOverlapTeams(accountManagerId);
      setIsOverLAP(currentUserTeams.data?.length > 0 ? 'yes' : 'no');
    };
    fetchData();
  }, [accountManagerId]);
  useEffect(() => {
    const fetchData = async () => {
      const jobResponse = await getJobById(jobId);
      const accountManager = (await getUserByEmail(jobResponse?.data?.accountManagerEmail))?.data;
      setAccountManagerName(accountManager?.firstName);
      const currentUserTeams = await getOverlapTeams(jobResponse?.data?.accountManagerEmail);
      setIsOverLAP(currentUserTeams?.data?.length > 0 ? 'yes' : 'no');
    };
    fetchData();
  }, [jobId]);

  useEffect(() => {
    const fetchDataLog = async () => {
      try {
        const { data: activityResponseEntity } = await getRecruiterActivitLogs(
          accountManagerId ?? ''
        );
        setActivityLogData(activityResponseEntity);
        const updatedActivityLists = activityResponseEntity.map((data) => ({
          activity: (
            <TimelineItem key={data.id}>
              <Grid container>
                <Grid item xs={12}>
                  <WsTypography color={WsTypoColors.TERTIARY}>
                    {` At ${formatDateInDayMonthYearTime(data.createdOn.toString())}`}
                  </WsTypography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant={WsTypoVariants.SUB_TITLE}>
                    {`${data.field} Category -> ${data.description} client ${data.parentEntitiyName}`}
                  </Typography>
                </Grid>
              </Grid>
            </TimelineItem>
          ),
          leftChildren: <CircleIcon sx={{ color: WsColors.TheBlues600 }} fontSize="small" />,
        }));

        setActivityLists(updatedActivityLists);
      } catch {
        setNotification(failed(`Failed to fetch company Information`));
      }
    };
    fetchDataLog();
  }, [accountManagerId, setNotification]);

  return (
    <>
      <Box>
        <WsBreadCrumbs
          breadCrumbsList={getDashBoardBreadCrumbList(
            IDashBoardBreadCrumbTypes.ACCOUNT_MANAGERS,
            accountManagerName ?? ''
          )}
        />
        <WsTitleBanner
          title={accountManagerName ?? ''}
          backButton={false}
          rightChildren={
            <PinDashboardCollectionButton
              filters={getFilters}
              entityId={accountManagerId}
              entityName={accountManagerName}
            />
          }
        />
      </Box>
      <Grid item borderRadius={2} sx={{ background: WsColors.White }} mt={2} mb={1}>
        <ScrollableButtonTabs
          scrollList={filteredTabNames(true)}
          getTabIndex={(index: number) => setTabIndex(index)}
          defaultValue={tabIndex}
        />
      </Grid>
      {tabIndex == 0 && (
        <>
          {isOverLAP == 'yes' ? (
            <UserProfileOverview tabIndex={tabIndex} accountManager={accountManagerId ?? ''} />
          ) : (
            <Grid>
              <AccessControl errorText="You cannot access analytics data as the account manager is not part of any mutual teams" />
            </Grid>
          )}
        </>
      )}
      {tabIndex == 1 && (
        <WsTabs>
          <WsTabs.Item label="Notes">
            <ComposeNote
              parent={NotesParent.USERPROFILE}
              parentId={''}
              isClient={true}
              email={accountManagerId ?? ''}
            />
          </WsTabs.Item>
          <WsTabs.Item label="Activities">
            {activityLogData && (
              <Grid item xs={12} borderRadius={2} pl={2} sx={{ background: WsColors.White }}>
                <Timeline>
                  <Box ml="-100%">
                    <WsTimeLine activityList={activityLists} timeLineDotVariant={'outlined'} />
                  </Box>
                </Timeline>
              </Grid>
            )}
          </WsTabs.Item>
        </WsTabs>
      )}
    </>
  );
};
export default AccountManagerDetailsPage;
